/**
 * The Excel-style duty roster.
 *
 * Rows are employees, columns are dates, each cell holds a short shift code —
 * the shape a duty roster is already kept in on paper, which is the point of the
 * requirement. The existing /roster/add screen is a week-paged form over the
 * same data; this is the sheet view of it.
 *
 * Interaction notes worth knowing before changing anything here:
 *
 * - Edits are held in `pending` (a keyed diff) and only sent on Save, so the
 *   grid stays responsive and the request carries three cells rather than three
 *   thousand. `pending` is keyed `employeeId|date`, the same key the server's
 *   cell deltas use.
 * - Selection is a rectangle of (row, column) indices, so bulk apply and copy
 *   work on what the user can see rather than on ids we would have to track
 *   separately.
 * - A cell the server marked `is_locked` (worked, or in the past) is read-only
 *   everywhere — keyboard, paste and import all skip it. The server refuses
 *   them too; this is convenience, not the boundary.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, Upload } from "antd";
import dayjs from "dayjs";
import {
    BarChart3, CalendarClock, CheckCircle2, Copy, Download, Eye, History, Loader2, PlaneTakeoff,
    Rewind, Save, Sheet, UploadCloud, Wand2, X,
} from "lucide-react";

import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, formatTimeRangeToAMPM, formatTimeToAMPM } from "../../components/common/CommonFunctions";
import { colorForShift } from "../../components/common/HierarchyShift";
import useApiMethods from "../../lib/useApiMethods";
import { DUTY_ROSTER, LEAVE_REQUEST_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { toolbarInputClass, toolbarPickerClass } from "../../components/ui/fieldStyles";
import { downloadSheet, parseSheet } from "./dutyRosterSheet";
import RosterBreadcrumb from "./RosterBreadcrumb";
import ShiftLegend from "./ShiftLegend";
import { LeaveApprovalReviewDialog } from "./LeaveApprovalReviewDialog";

const ISO = "YYYY-MM-DD";
const cellKey = (employeeId, date) => `${employeeId}|${date}`;

/** Match Attendance Log column sizing — table scrolls horizontally instead of compressing. */
const EMPLOYEE_COL_W = 300;
/** w-10 cell + sm:px-6 padding each side (same as Reactable day columns in Attendance Log). */
const DAY_COL_W = 88;

const mapEmployeeForDetails = (employee) => ({
    ...employee,
    first_name: employee?.employee_name || "",
    designation_id: employee?.designation
        ? { id: employee.designation.id, designation_name: employee.designation.name }
        : { id: 0, designation_name: "" },
});

const stickyEmployeeShadow = (scrolledX) =>
    scrolledX > 0 ? "shadow-[4px_0_10px_-2px_rgba(0,0,0,0.12)] dark:shadow-[4px_0_10px_-2px_rgba(0,0,0,0.45)]" : "";

/** Week off / leave are picked from the same popover as a shift. */
const SPECIAL_OPTIONS = [
    { value: "weekoff", code: "WO", label: "Week off" },
    { value: "leave", code: "L", label: "Leave" },
    { value: "blank", code: "—", label: "Clear (no shift)" },
];

const KEEP = "__keep__";

/** The value a cell shows, pending edit first, then what the server sent. */
const resolveCell = (cell, pending) => {
    const key = cellKey(cell.employee_id, cell.date);
    const edit = pending[key];
    if (!edit) return { ...cell, dirty: false };
    return {
        ...cell,
        shift_id: edit.shift,
        shift_name: null,
        shift_timings_from: null,
        shift_timings_to: null,
        is_weekoff: edit.is_weekoff,
        is_leave: edit.is_leave,
        dirty: true,
    };
};

/** Leave keeps its own shift. Neighbours are only a fallback when this cell has none. */
const inferShiftIdForLeave = (cell, rowCells) => {
    if (!cell?.is_leave) return cell?.shift_id || null;
    if (cell.shift_id) return cell.shift_id;
    const idx = rowCells?.findIndex((item) => item.date === cell.date) ?? -1;
    if (idx >= 0) {
        for (let dist = 1; dist < rowCells.length; dist += 1) {
            for (const j of [idx - dist, idx + dist]) {
                const neighbor = rowCells[j];
                if (neighbor?.shift_id && !neighbor.is_weekoff && !neighbor.is_holiday && !neighbor.is_leave) {
                    return neighbor.shift_id;
                }
            }
        }
    }
    return null;
};

const cellHasOpenLeaveRequest = (cell) => Boolean(cell?.leave_request || cell?.has_leave_request);

const cellShowsRosterLeave = (cell) => Boolean(
    cell?.is_leave && cell?.leave_request?.approval_status !== "pending",
);

const tokenFor = (cell, shiftsById) => {
    if (cell.is_holiday) return "H";
    if (cell.is_weekoff) return "WO";
    if (cellShowsRosterLeave(cell)) return "L";
    if (!cell.shift_id) return "";
    return shiftsById[cell.shift_id]?.code || "?";
};

const formatShiftTime = (value) => (value ? formatTimeToAMPM(value) : null);

const SOURCE_LABELS = {
    roster: "Published roster",
    override: "Shift change override",
    default: "Employee default shift",
    none: "Not assigned",
};

const LEAVE_REQUEST_TYPE_LABELS = {
    leave: "Leave",
    work_from_home: "Work from home",
    permission: "Permission",
};

const LEAVE_REQUEST_STATUS_LABELS = {
    pending: "Pending",
    approved: "Approved",
    "cancel inprogress": "Cancel in progress",
};

const formatLeaveRequestDates = (request) => {
    const from = request.from_date ? dayjs(request.from_date) : null;
    const to = request.to_date ? dayjs(request.to_date) : from;
    if (!from || !from.isValid()) return "";
    if (!to || !to.isValid() || from.isSame(to, "day")) {
        return from.format("D MMM YYYY");
    }
    return `${from.format("D MMM")} – ${to.format("D MMM YYYY")}`;
};

const leaveRequestTypeText = (request) => {
    const typeLabel = LEAVE_REQUEST_TYPE_LABELS[request.request_type]
        || request.request_type
        || "Leave";
    if (request.request_type === "leave" && request.leave_type) {
        return `${typeLabel} · ${request.leave_type}`;
    }
    return typeLabel;
};

const markLeaveRequestRejectedOnGrid = (prev, request) => {
    if (!request?.id) return prev;
    return {
        ...prev,
        result: (prev.result || []).map((row) => ({
            ...row,
            cells: (row.cells || []).map((cell) => {
                if (cell.leave_request?.id !== request.id) return cell;
                return {
                    ...cell,
                    has_leave_request: false,
                    is_leave: false,
                    leave_label: null,
                    leave_request: null,
                };
            }),
        })),
    };
};

const markLeaveRequestApprovedOnGrid = (prev, request) => {
    if (!request?.id) return prev;
    const isLeaveType = (request.request_type || "leave") === "leave";
    return {
        ...prev,
        result: (prev.result || []).map((row) => ({
            ...row,
            cells: (row.cells || []).map((cell) => {
                if (cell.leave_request?.id !== request.id) return cell;
                return {
                    ...cell,
                    has_leave_request: true,
                    is_leave: isLeaveType ? true : cell.is_leave,
                    leave_label: isLeaveType
                        ? (request.leave_type || cell.leave_label)
                        : cell.leave_label,
                    leave_request: {
                        ...cell.leave_request,
                        approval_status: "approved",
                    },
                };
            }),
        })),
    };
};

const TooltipLine = ({ label, children }) => {
    if (!children) return null;
    return (
        <div className="flex gap-1.5 leading-snug">
            <span className="shrink-0 text-muted-foreground">{label}:</span>
            <span>{children}</span>
        </div>
    );
};

/** Rich hover card for a grid cell — one shared instance, not one per cell. */
const CellTooltipContent = ({ cell, shiftsById, editable, fallbackShiftId }) => {
    const shiftId = cell.shift_id || fallbackShiftId || null;
    const shiftMeta = shiftId ? shiftsById[shiftId] : null;
    const code = shiftMeta?.code || (shiftId ? "?" : tokenFor(cell, shiftsById) || "—");
    const name = shiftMeta?.name || (!cell.dirty ? cell.shift_name : null);
    const timeFrom = formatShiftTime(shiftMeta?.shift_timings_from || (!cell.dirty ? cell.shift_timings_from : null));
    const timeTo = formatShiftTime(shiftMeta?.shift_timings_to || (!cell.dirty ? cell.shift_timings_to : null));
    const timings = timeFrom && timeTo ? `${timeFrom} – ${timeTo}` : null;
    const sourceLabel = SOURCE_LABELS[cell.source] || cell.source;
    const showShiftDetails = !cell.is_holiday && Boolean(shiftMeta || timings);
    const isNight = Boolean(shiftMeta?.is_night_shift);

    let assignment = null;
    if (cell.is_holiday) {
        assignment = "Company holiday";
    } else if (cell.is_weekoff) {
        assignment = "Week off";
    } else if (cellShowsRosterLeave(cell)) {
        assignment = cell.leave_label ? `Leave (${cell.leave_label})` : "Leave";
    } else if (cell.is_pending || !cell.shift_id) {
        assignment = "No shift assigned";
    } else if (name) {
        assignment = name;
    } else {
        assignment = code;
    }

    const statusNotes = [
        cell.dirty && "Unsaved change",
        cell.is_locked && (dayjs(cell.date).isBefore(dayjs(), "day")
            ? "Locked — cannot change a past date"
            : "Locked — already worked"),
        cell.source === "override" && !cell.dirty && "Shift change recorded",
        editable && !cell.is_locked && !cell.dirty && "Double-click or Enter to edit",
    ].filter(Boolean);

    return (
        <div className="space-y-1.5 text-xs">
            <p className="font-semibold text-foreground">
                {dayjs(cell.date).format("ddd, D MMM YYYY")}
            </p>
            <p className="font-medium text-foreground">{assignment}</p>
            {showShiftDetails && (
                <>
                    {cellShowsRosterLeave(cell) && (
                        <TooltipLine label="Shift">{name ? `${code} · ${name}` : code}</TooltipLine>
                    )}
                    <TooltipLine label="Timings">{timings}</TooltipLine>
                    {isNight && (
                        <TooltipLine label="Type">Night shift</TooltipLine>
                    )}
                    <TooltipLine label="Source">{sourceLabel}</TooltipLine>
                </>
            )}
            {(cell.is_weekoff || cellShowsRosterLeave(cell)) && !showShiftDetails && cell.source && cell.source !== "none" && (
                <TooltipLine label="Source">{sourceLabel}</TooltipLine>
            )}
            {statusNotes.length > 0 && (
                <div className="space-y-0.5 border-t border-border/60 pt-1.5 text-[11px] text-muted-foreground">
                    {statusNotes.map((note) => (
                        <p key={note}>{note}</p>
                    ))}
                </div>
            )}
        </div>
    );
};

const DutyRoster = () => {
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();
    const { add: addPermission, edit: editPermission } =
        checkUserPermissionFunc("Roster", "Duty Roster", "SubMenu");
    const { permission: canViewReport } = checkUserPermissionFunc("reports", "roster_report", "SubMenu");
    const canEditPermission = !!(addPermission || editPermission);

    // The pending-shift alert links here with the date it is complaining about,
    // so the planner lands on that day rather than on this month and having to
    // find it. Falls back to the current month for a plain visit.
    const location = useLocation();
    const [range, setRange] = useState(() => {
        const { from_date: from, to_date: to } = location.state || {};
        if (from && to) return [dayjs(from), dayjs(to)];
        return [dayjs().startOf("month"), dayjs().endOf("month")];
    });
    const [department, setDepartment] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState({ page_no: 1, page_size: 25 });

    const [grid, setGrid] = useState({ dates: [], shifts: [], result: [], roster: null });
    const [meta, setMeta] = useState({
        total_count: 0, no_of_pages: 1, feature_enabled: false, can_edit: false,
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);

    // Keyed diff of unsaved edits: `employeeId|date` -> {shift, is_weekoff, is_leave}
    const [pending, setPending] = useState({});
    // Rectangle of grid indices, or null.
    const [selection, setSelection] = useState(null); // {r1,c1,r2,c2}
    const [anchor, setAnchor] = useState(null);       // {r,c}
    const [picker, setPicker] = useState(null);       // {r,c}
    const [pickerTab, setPickerTab] = useState("shift");
    const [approvingLeaveId, setApprovingLeaveId] = useState(null);
    const [leaveReview, setLeaveReview] = useState(null);
    const [leaveReviewNotes, setLeaveReviewNotes] = useState("");
    const [leaveReviewLoading, setLeaveReviewLoading] = useState(false);
    const [leaveRevert, setLeaveRevert] = useState(null);
    const [leaveRevertReason, setLeaveRevertReason] = useState("");
    const [leaveRevertLoading, setLeaveRevertLoading] = useState(false);
    const [importPreview, setImportPreview] = useState(null);
    const [gridScrolledX, setGridScrolledX] = useState(0);
    const [cellTip, setCellTip] = useState(null);
    const gridRef = useRef(null);

    const todayIso = dayjs().format(ISO);
    const tableMinWidth = EMPLOYEE_COL_W + grid.dates.length * DAY_COL_W;

    const shiftsById = useMemo(
        () => Object.fromEntries(grid.shifts.map((s) => [s.id, s])),
        [grid.shifts],
    );
    const dirtyCount = Object.keys(pending).length;
    const canEdit = canEditPermission && meta.can_edit;

    // ---------------------------------------------------------------- fetching
    const loadGrid = useCallback(async (signal) => {
        const response = await fetch(DUTY_ROSTER, {
            from_date: range[0].format(ISO),
            to_date: range[1].format(ISO),
            department: department || undefined,
            search: search || undefined,
            page_no: page.page_no,
            page_size: page.page_size,
        }, setLoading, signal);
        if (response?.status === "cancelled") return;

        if (response?.status !== "success") {
            ToastMsg("error", response?.message || "Failed to load the duty roster");
            return;
        }
        const data = response.data || {};
        setGrid({
            dates: data.dates || [],
            shifts: data.shifts || [],
            result: data.result || [],
            roster: data.roster || null,
        });
        setMeta({
            total_count: data.total_count || 0,
            no_of_pages: data.no_of_pages || 1,
            feature_enabled: !!data.feature_enabled,
            can_edit: !!data.can_edit,
        });
        // Reloading replaces what the diff was computed against, so keeping it
        // would apply edits to whatever now sits in those positions.
        setPending({});
        setSelection(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, department, search, page.page_no, page.page_size, serverTrigger]);

    useEffect(() => {
        const controller = new AbortController();
        loadGrid(controller.signal);
        return () => controller.abort();
    }, [loadGrid]);

    useEffect(() => {
        const el = gridRef.current;
        if (!el) return undefined;
        const onScroll = () => {
            setGridScrolledX(el.scrollLeft);
            setCellTip(null);
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, [loading, grid.result.length]);

    // Hide the cell tooltip while a modal is open; Escape closes the review, then the picker.
    useEffect(() => {
        if (!picker) {
            setPickerTab("shift");
            setApprovingLeaveId(null);
            if (!leaveReview && !leaveRevert) return undefined;
        }
        setCellTip(null);
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                if (leaveRevert) {
                    setLeaveRevert(null);
                    return;
                }
                if (leaveReview) {
                    setLeaveReview(null);
                    return;
                }
                setPicker(null);
            }
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [picker, leaveReview, leaveRevert]);

    // ---------------------------------------------------------------- editing
    const cellAt = useCallback((r, c) => {
        const row = grid.result[r];
        const cell = row?.cells?.[c];
        if (!row || !cell) return null;
        return { row, cell: resolveCell(cell, pending) };
    }, [grid.result, pending]);

    const isEditable = useCallback((r, c) => {
        const found = cellAt(r, c);
        if (!found) return false;
        return canEdit && found.row.can_edit && !found.cell.is_locked && !found.cell.is_holiday;
    }, [cellAt, canEdit]);

    const explainWhyNotEditable = useCallback((found) => {
        if (!found) return;
        if (found.cell.is_holiday) {
            ToastMsg("error", "Cannot change shift on a holiday");
            return;
        }
        if (found.cell.is_locked) {
            const isPast = found.cell.date && dayjs(found.cell.date).isBefore(dayjs(), "day");
            ToastMsg(
                "error",
                isPast
                    ? "Cannot change shift for a past date"
                    : "This day is locked because it has already been worked",
            );
        }
    }, []);

    const showCellTipAt = useCallback((r, c, source = "keyboard") => {
        const el = gridRef.current?.querySelector(`[data-roster-cell="${r}-${c}"]`);
        const found = cellAt(r, c);
        if (!el || !found) return;
        const rect = el.getBoundingClientRect();
        const rowCells = (grid.result[r]?.cells || []).map((item) => resolveCell(item, pending));
        setCellTip({
            top: rect.top,
            left: rect.left + rect.width / 2,
            cell: found.cell,
            editable: isEditable(r, c),
            fallbackShiftId: inferShiftIdForLeave(found.cell, rowCells),
            source,
        });
    }, [cellAt, isEditable, grid.result, pending]);

    const applyToCells = useCallback((targets, value) => {
        const writable = [];
        const skippedLeave = [];
        targets.forEach(({ r, c }) => {
            if (!isEditable(r, c)) return;
            const found = cellAt(r, c);
            if (!found) return;
            if (cellHasOpenLeaveRequest(found.cell)) {
                skippedLeave.push({
                    date: found.cell.date,
                    status: found.cell.leave_request?.approval_status,
                });
                return;
            }
            writable.push(found);
        });
        const approvedDates = [...new Set(
            skippedLeave
                .filter((item) => item.status === "approved" && item.date)
                .map((item) => item.date),
        )];
        const otherDates = [...new Set(
            skippedLeave
                .filter((item) => item.status !== "approved" && item.date)
                .map((item) => item.date),
        )];
        const formatDates = (dates) => dates.map((date) => dayjs(date).format("D MMM YYYY")).join(", ");
        if (!writable.length) {
            if (approvedDates.length) {
                ToastMsg(
                    "error",
                    approvedDates.length === 1
                        ? `Revert the approved leave on ${formatDates(approvedDates)} before changing the shift.`
                        : `Revert the approved leave on ${formatDates(approvedDates)} before changing those days.`,
                );
                setPickerTab("leave");
            } else if (skippedLeave.length) {
                ToastMsg(
                    "error",
                    "Cannot change days that already have a leave request. Cancel or reject it first.",
                );
                setPickerTab("leave");
            }
            return false;
        }
        setPending((prev) => {
            const next = { ...prev };
            writable.forEach((found) => {
                const key = cellKey(found.cell.employee_id, found.cell.date);
                next[key] = {
                    employee: found.cell.employee_id,
                    date: found.cell.date,
                    shift: value.shift === KEEP ? (found.cell.shift_id ?? null) : (value.shift ?? null),
                    is_weekoff: !!value.is_weekoff,
                    is_leave: value.is_leave === KEEP ? !!found.cell.is_leave : !!value.is_leave,
                };
            });
            return next;
        });
        if (approvedDates.length) {
            ToastMsg(
                "success",
                `Approved leave on ${formatDates(approvedDates)} was excluded. Revert it before changing those days. All other selected days were applied.`,
            );
        } else if (otherDates.length) {
            ToastMsg(
                "success",
                otherDates.length === 1
                    ? `Leave request on ${formatDates(otherDates)} was excluded. All other selected days were applied.`
                    : `Leave requests on ${formatDates(otherDates)} were excluded. All other selected days were applied.`,
            );
        }
        return true;
    }, [cellAt, isEditable]);

    const selectionTargets = useMemo(() => {
        if (!selection) return [];
        const targets = [];
        for (let r = Math.min(selection.r1, selection.r2); r <= Math.max(selection.r1, selection.r2); r += 1) {
            for (let c = Math.min(selection.c1, selection.c2); c <= Math.max(selection.c1, selection.c2); c += 1) {
                targets.push({ r, c });
            }
        }
        return targets;
    }, [selection]);

    const pickerTargets = useMemo(() => {
        if (!picker) return [];
        const inSelection = selection && selectionTargets.some(
            (target) => target.r === picker.r && target.c === picker.c,
        );
        return inSelection && selectionTargets.length
            ? selectionTargets
            : [{ r: picker.r, c: picker.c }];
    }, [picker, selection, selectionTargets]);

    const pickerShifts = useMemo(() => {
        if (!picker) return grid.shifts;
        let allowed = null;
        for (const { r } of pickerTargets) {
            const rowAllowed = grid.result[r]?.allowed_shift_ids;
            if (!Array.isArray(rowAllowed) || !rowAllowed.length) continue;
            if (allowed === null) {
                allowed = new Set(rowAllowed);
            } else {
                allowed = new Set([...allowed].filter((id) => rowAllowed.includes(id)));
            }
        }
        if (allowed === null) return grid.shifts;
        return grid.shifts.filter((shift) => allowed.has(shift.id));
    }, [picker, pickerTargets, grid.shifts, grid.result]);

    const pickerLeaveRequests = useMemo(() => {
        if (!picker) return [];
        const byId = new Map();
        pickerTargets.forEach(({ r, c }) => {
            const found = cellAt(r, c);
            const request = found?.cell?.leave_request;
            if (!request?.id) return;
            const employeeName = found.row?.employee?.employee_name
                || found.row?.employee?.employee_id
                || "Employee";
            if (!byId.has(request.id)) {
                byId.set(request.id, { ...request, employee_name: employeeName });
            }
        });
        return Array.from(byId.values()).sort((a, b) =>
            String(a.employee_name).localeCompare(String(b.employee_name)),
        );
    }, [picker, pickerTargets, cellAt]);

    const loadLeaveApprovalRow = async (request) => {
        const response = await fetch(LEAVE_REQUEST_URL, { leave_id: request.id, page_size: 1 });
        const row = response?.data?.result?.[0];
        if (response?.status !== "success" || !row) {
            ToastMsg("error", response?.message || "Failed to load the leave request");
            return null;
        }
        return row;
    };

    const openLeaveReview = async (request) => {
        if (!request?.id || approvingLeaveId || leaveReviewLoading || leaveRevertLoading) return;
        setApprovingLeaveId(request.id);
        const row = await loadLeaveApprovalRow(request);
        if (row) {
            setLeaveReview(row);
            setLeaveReviewNotes("");
        }
        setApprovingLeaveId(null);
    };

    const openLeaveRevert = async (request) => {
        if (!request?.id || approvingLeaveId || leaveRevertLoading) return;
        setApprovingLeaveId(request.id);
        const row = await loadLeaveApprovalRow(request);
        if (row) {
            setLeaveRevert(row);
            setLeaveRevertReason("");
        }
        setApprovingLeaveId(null);
    };

    const confirmLeaveReview = async () => {
        if (!leaveReview?.id || leaveReviewLoading) return;
        setLeaveReviewLoading(true);
        const payload = {
            ...leaveReview,
            approval_notes: leaveReviewNotes,
            approval_status: leaveReview.request_type === "cancel request" ? "request cancelled" : "approved",
            company: leaveReview.company != null ? String(leaveReview.company) : null,
            leave_type: leaveReview.leave_type_id || null,
            leave_id: leaveReview.leave_id || leaveReview.id,
        };
        delete payload.leave_type_id;
        delete payload.employee;
        const response = await update(LEAVE_REQUEST_URL, payload);
        if (response?.status === "success") {
            ToastMsg("success", response.message || "Leave request approved");
            setGrid((prev) => markLeaveRequestApprovedOnGrid(prev, {
                id: leaveReview.id,
                request_type: leaveReview.leave_request_type || "leave",
                leave_type: typeof leaveReview.leave_type === "string" ? leaveReview.leave_type : null,
            }));
            setLeaveReview(null);
            setLeaveReviewNotes("");
        } else {
            ToastMsg("error", response?.message || "Failed to approve the request");
        }
        setLeaveReviewLoading(false);
    };

    const confirmLeaveReject = async () => {
        if (!leaveReview?.id || leaveReviewLoading) return;
        if (!leaveReviewNotes.trim()) {
            ToastMsg("error", "Please provide a reason for rejection");
            return;
        }
        setLeaveReviewLoading(true);
        const payload = {
            ...leaveReview,
            approval_notes: leaveReviewNotes,
            approval_status: "rejected",
            request_type: "leave request",
            company: leaveReview.company != null ? String(leaveReview.company) : null,
            leave_type: leaveReview.leave_type_id || null,
            leave_id: leaveReview.leave_id || leaveReview.id,
        };
        delete payload.leave_type_id;
        delete payload.employee;
        const response = await update(LEAVE_REQUEST_URL, payload);
        if (response?.status === "success") {
            ToastMsg("success", response.message || "Leave request rejected");
            setGrid((prev) => markLeaveRequestRejectedOnGrid(prev, { id: leaveReview.id }));
            setLeaveReview(null);
            setLeaveReviewNotes("");
        } else {
            ToastMsg("error", response?.message || "Failed to reject the request");
        }
        setLeaveReviewLoading(false);
    };

    const confirmLeaveRevert = async () => {
        if (!leaveRevert?.id || leaveRevertLoading) return;
        setLeaveRevertLoading(true);
        const payload = {
            ...leaveRevert,
            approval_notes: leaveRevertReason,
            approval_status: "cancelled",
            request_type: "cancel request",
            company: leaveRevert.company != null ? String(leaveRevert.company) : null,
            leave_type: leaveRevert.leave_type_id || null,
            leave_id: leaveRevert.leave_id || leaveRevert.id,
        };
        delete payload.leave_type_id;
        delete payload.employee;
        const response = await update(LEAVE_REQUEST_URL, payload);
        if (response?.status === "success") {
            ToastMsg("success", response.message || "Leave request reverted");
            setGrid((prev) => markLeaveRequestRejectedOnGrid(prev, { id: leaveRevert.id }));
            setLeaveRevert(null);
            setLeaveRevertReason("");
        } else {
            ToastMsg("error", response?.message || "Failed to revert the leave request");
        }
        setLeaveRevertLoading(false);
    };

    const valueFromChoice = (choice) => {
        if (choice === "weekoff") return { shift: null, is_weekoff: true, is_leave: false };
        if (choice === "leave") return { shift: KEEP, is_weekoff: false, is_leave: true };
        if (choice === "blank") return { shift: null, is_weekoff: false, is_leave: false };
        return { shift: choice, is_weekoff: false, is_leave: KEEP };
    };

    const handlePick = (choice) => {
        const value = valueFromChoice(choice);
        const applied = applyToCells(
            pickerTargets.length ? pickerTargets : [{ r: picker.r, c: picker.c }],
            value,
        );
        if (applied) setPicker(null);
    };

    const handleCellMouseDown = (r, c, event) => {
        // Stop the browser treating Shift+click as a text-selection gesture.
        event.preventDefault();
        window.getSelection()?.removeAllRanges();
        gridRef.current?.focus({ preventScroll: true });

        if (event.shiftKey && anchor) {
            setSelection({ r1: anchor.r, c1: anchor.c, r2: r, c2: c });
            showCellTipAt(r, c, "keyboard");
            return;
        }
        setAnchor({ r, c });
        setSelection({ r1: r, c1: c, r2: r, c2: c });
        showCellTipAt(r, c, "keyboard");
    };

    /** Copy the anchor row's pattern across the whole selection's rows. */
    const handleCopyPattern = () => {
        if (!selection || !anchor) return;
        const sourceRow = anchor.r;
        const c1 = Math.min(selection.c1, selection.c2);
        const c2 = Math.max(selection.c1, selection.c2);
        const r1 = Math.min(selection.r1, selection.r2);
        const r2 = Math.max(selection.r1, selection.r2);

        const pattern = [];
        for (let c = c1; c <= c2; c += 1) {
            const found = cellAt(sourceRow, c);
            pattern.push(found ? {
                shift: found.cell.shift_id || null,
                is_weekoff: !!found.cell.is_weekoff,
                is_leave: !!found.cell.is_leave,
            } : null);
        }

        setPending((prev) => {
            const next = { ...prev };
            for (let r = r1; r <= r2; r += 1) {
                if (r === sourceRow) continue;
                pattern.forEach((value, index) => {
                    const c = c1 + index;
                    if (!value || !isEditable(r, c)) return;
                    const found = cellAt(r, c);
                    if (!found || cellHasOpenLeaveRequest(found.cell)) return;
                    next[cellKey(found.cell.employee_id, found.cell.date)] = {
                        employee: found.cell.employee_id,
                        date: found.cell.date,
                        ...value,
                    };
                });
            }
            return next;
        });
        ToastMsg("info", "Pattern copied down the selection");
    };

    // Keyboard: arrows move, Enter opens the picker, a shift's first letter
    // types it straight in, Escape clears. Bound on the grid container so it
    // only applies while the grid has focus.
    const handleKeyDown = (event) => {
        if (!anchor) return;
        const { key } = event;
        const move = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[key];
        if (move) {
            event.preventDefault();
            const r = Math.min(Math.max(anchor.r + move[0], 0), grid.result.length - 1);
            const c = Math.min(Math.max(anchor.c + move[1], 0), grid.dates.length - 1);
            setAnchor({ r, c });
            setSelection(event.shiftKey
                ? { r1: selection?.r1 ?? anchor.r, c1: selection?.c1 ?? anchor.c, r2: r, c2: c }
                : { r1: r, c1: c, r2: r, c2: c });
            const el = gridRef.current?.querySelector(`[data-roster-cell="${r}-${c}"]`);
            el?.scrollIntoView({ block: "nearest", inline: "nearest" });
            showCellTipAt(r, c, "keyboard");
            return;
        }
        if (key === "Escape") {
            event.preventDefault();
            if (picker) {
                setPicker(null);
                return;
            }
            setSelection(null);
            setCellTip(null);
            return;
        }
        if (key === "Enter") {
            event.preventDefault();
            if (isEditable(anchor.r, anchor.c)) {
                setCellTip(null);
                setPicker({ r: anchor.r, c: anchor.c });
            } else {
                explainWhyNotEditable(cellAt(anchor.r, anchor.c));
            }
            return;
        }
        if (key === "Delete" || key === "Backspace") {
            event.preventDefault();
            applyToCells(selectionTargets.length ? selectionTargets : [anchor], valueFromChoice("blank"));
            return;
        }
        if (key.length === 1) {
            const typed = key.toUpperCase();
            const shift = grid.shifts.find((s) => (s.code || "").startsWith(typed));
            const targets = selectionTargets.length ? selectionTargets : [anchor];
            if (typed === "W") { applyToCells(targets, valueFromChoice("weekoff")); return; }
            if (shift) { applyToCells(targets, valueFromChoice(shift.id)); }
        }
    };

    // ---------------------------------------------------------------- saving
    const buildCells = () => Object.values(pending);

    const save = async ({ publish = false, validateOnly = false, cells = null } = {}) => {
        const payload = {
            from_date: range[0].format(ISO),
            to_date: range[1].format(ISO),
            roster: grid.roster?.id || undefined,
            cells: cells || buildCells(),
            publish,
            validate_only: validateOnly,
        };
        if (!payload.cells.length && !publish) {
            ToastMsg("info", "There are no changes to save");
            return null;
        }

        setSaving(true);
        const response = await update(DUTY_ROSTER, payload);
        setSaving(false);

        if (response?.status !== "success") {
            const firstError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", firstError || response?.message || "Failed to save the duty roster");
            return null;
        }
        return response.data;
    };

    const reportResult = (data) => {
        ToastMsg("success", `${data.applied} cell${data.applied === 1 ? "" : "s"} saved`);
        (data.warnings || []).forEach((warning) => ToastMsg("warning", warning));
        if (data.skipped?.length) {
            ToastMsg("warning", `${data.skipped.length} cell(s) skipped — ${data.skipped[0].reason}`);
        }
    };

    const handleSave = async (publish) => {
        const data = await save({ publish });
        if (!data) return;
        reportResult(data);
        setPending({});
        setServerTrigger((prev) => !prev);
    };

    // ---------------------------------------------------------------- sheet
    const handleExport = () => {
        if (!grid.result.length) {
            ToastMsg("info", "Nothing to export");
            return;
        }
        downloadSheet(
            grid.result, grid.dates, grid.shifts,
            `duty-roster-${range[0].format(ISO)}-to-${range[1].format(ISO)}.xlsx`,
        );
    };

    const handleImport = async (file) => {
        try {
            const { cells, problems } = await parseSheet(file, {
                rows: grid.result, dates: grid.dates, shifts: grid.shifts,
            });
            if (!cells.length) {
                ToastMsg(problems.length ? "error" : "info",
                    problems[0]?.reason || "The sheet matches what is already on screen");
                if (problems.length) setImportPreview({ cells, problems, server: null });
                return false;
            }
            // Dry-run through the same endpoint that will write, so the preview
            // is produced by the rules that will be applied.
            const server = await save({ cells, validateOnly: true });
            setImportPreview({ cells, problems, server });
        } catch (error) {
            ToastMsg("error", "Could not read that file — is it a .xlsx or .csv sheet?");
        }
        return false; // antd Upload: never upload it itself
    };

    const confirmImport = async () => {
        const data = await save({ cells: importPreview.cells });
        setImportPreview(null);
        if (!data) return;
        reportResult(data);
        setServerTrigger((prev) => !prev);
    };

    // ---------------------------------------------------------------- render
    if (!meta.feature_enabled && !loading) {
        return (
            <ContainerCard>
                <RosterBreadcrumb items={[
                    { label: "Published Rosters", href: "/roster" },
                    { label: "Duty Roster" },
                ]} />
                <ModuleTitleCard Icon={Sheet} title="Duty Roster"
                    subtitle="Spreadsheet-style shift planning" />
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center px-4">
                    <CalendarClock className="w-10 h-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground max-w-md">
                        Roster &amp; shift management is switched off for this organization.
                    </p>
                    <p className="text-xs text-muted-foreground max-w-md">
                        Enable <strong>Roster &amp; shift management</strong> under Settings → Configuration → Features &amp; Access.
                    </p>
                    <Button
                        variant="brand"
                        size="sm"
                        className="mt-2"
                        onClick={() => navigate("/settings/configuration")}
                    >
                        Open Configuration
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    const inSelection = (r, c) => selection
        && r >= Math.min(selection.r1, selection.r2) && r <= Math.max(selection.r1, selection.r2)
        && c >= Math.min(selection.c1, selection.c2) && c <= Math.max(selection.c1, selection.c2);

    const rowsOnPage = grid.result.length;
    const rowRangeStart = meta.total_count && rowsOnPage
        ? (page.page_no - 1) * page.page_size + 1
        : 0;
    const rowRangeEnd = rowRangeStart ? rowRangeStart + rowsOnPage - 1 : 0;

    return (
        <ContainerCard scrollable className="relative">
            <RosterBreadcrumb items={[
                { label: "Published Rosters", href: "/roster" },
                { label: "Duty Roster" },
            ]} />

            <div className="flex flex-col gap-2 px-3 sm:flex-row sm:items-start sm:justify-between">
                <ModuleTitleCard
                    Icon={Sheet}
                    title="Duty Roster"
                    subtitle="Spreadsheet-style shift planning. Click a cell, drag to select, or import a sheet."
                    totalRecords={meta.total_count}
                />

                <div className="flex shrink-0 flex-wrap items-center justify-start gap-2 pb-1 sm:justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => navigate("/roster/add", {
                            state: {
                                roster_id: grid.roster?.id,
                                roster_from_date: range[0]?.format(ISO),
                                roster_to_date: range[1]?.format(ISO),
                                editAccess: canEditPermission,
                            },
                        })}
                    >
                        <History className="w-4 h-4" />
                        Legacy week view
                    </Button>
                    {canViewReport && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={() => navigate("/roster/report")}
                        >
                            <BarChart3 className="w-4 h-4" />
                            Roster Report
                        </Button>
                    )}
                </div>
            </div>


            {/* Controls — toolbar* fieldStyles lock every control to h-10 (40px) */}
            <div className="mx-3 mb-2 flex flex-col gap-3 rounded-lg border border-gray-200/80 bg-gray-50/50 px-3 py-3 dark:border-gray-700/80 dark:bg-gray-900/30 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    <DatePicker.RangePicker
                        format="DD-MM-YYYY"
                        value={range}
                        onChange={(value) => value?.[0] && value?.[1] && setRange(value)}
                        allowClear={false}
                        className={toolbarPickerClass("w-[260px] shrink-0")}
                    />
                    <Input
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        placeholder="Search department"
                        className={toolbarInputClass("w-[200px] shrink-0")}
                    />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name or code"
                        className={toolbarInputClass("w-[200px] shrink-0")}
                    />
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-3 lg:justify-end">
                    <Button variant="outline" onClick={handleExport} className="gap-1.5">
                        <Download className="w-4 h-4" /> Export
                    </Button>
                    {canEdit && (
                        <Upload beforeUpload={handleImport} showUploadList={false}
                            accept=".xlsx,.xls,.csv">
                            <Button variant="outline" className="gap-1.5">
                                <UploadCloud className="w-4 h-4" /> Import
                            </Button>
                        </Upload>
                    )}
                    {canEdit && (
                        <Button
                            variant="outline"
                            onClick={handleCopyPattern}
                            disabled={!selection || saving}
                            className="gap-1.5"
                            title="Copy the first selected row's pattern to the other selected rows"
                        >
                            <Copy className="w-4 h-4" /> Copy pattern
                        </Button>
                    )}
                </div>
            </div>

            <ShiftLegend shifts={grid.shifts} roster={grid.roster} />

            {canEdit && (
                <div className="mx-3 mb-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sidebar/20 bg-sidebar/5 px-4 py-2 dark:border-sidebar/30 dark:bg-sidebar/10">
                    <span className="text-xs font-medium text-muted-foreground">
                        {dirtyCount
                            ? `${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}`
                            : "Edits are held locally until you save"}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setPending({}); setSelection(null); }}
                            disabled={!dirtyCount || saving}
                            className="gap-1"
                        >
                            <X className="w-4 h-4" /> Discard
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => handleSave(false)}
                            disabled={!dirtyCount || saving}
                            className="gap-1 bg-sidebar hover:bg-sidebar-primary text-white"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => handleSave(true)}
                            disabled={saving}
                            className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                            <CheckCircle2 className="w-4 h-4" /> Publish
                        </Button>
                    </div>
                </div>
            )}

            {/* Grid — horizontal scroll like Attendance Log; employee column stays fixed */}
            <div className="px-3 pb-3">
                <div
                    ref={gridRef}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    className="relative overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 bg-white outline-none select-none focus:ring-2 focus:ring-sidebar/40 dark:border-gray-700 dark:bg-gray-900"
                    style={{ maxHeight: "calc(100vh - 430px)" }}
                    onSelectStart={(e) => e.preventDefault()}
                >
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading duty roster…
                        </div>
                    ) : !grid.result.length ? (
                        <div className="py-16 text-center text-sm text-muted-foreground">
                            No employees match the current filters.
                        </div>
                    ) : (
                        <table
                            className="w-max select-none border-collapse text-xs divide-y divide-gray-200 dark:divide-gray-700"
                            style={{ minWidth: tableMinWidth }}
                        >
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th
                                        scope="col"
                                        className={cn(
                                            "sticky left-0 top-0 z-40 border-b border-r border-gray-200 bg-gray-50 px-3 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300",
                                            stickyEmployeeShadow(gridScrolledX),
                                        )}
                                        style={{ width: EMPLOYEE_COL_W, minWidth: EMPLOYEE_COL_W, maxWidth: EMPLOYEE_COL_W }}
                                    >
                                        Employee
                                    </th>
                                    {grid.dates.map((d) => {
                                        const isToday = d.date === todayIso;
                                        return (
                                            <th
                                                key={d.date}
                                                scope="col"
                                                className={cn(
                                                    "sticky top-0 z-20 border-b border-r border-gray-200 px-3 py-3 text-center text-xs font-medium tracking-wider whitespace-nowrap dark:border-gray-700 sm:px-6",
                                                    d.is_holiday
                                                        ? "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300"
                                                        : d.is_weekend
                                                            ? "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                                            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300",
                                                    isToday && "ring-2 ring-inset ring-sidebar/50",
                                                )}
                                                style={{ width: DAY_COL_W, minWidth: DAY_COL_W, maxWidth: DAY_COL_W }}
                                                title={d.is_holiday ? "Company holiday" : isToday ? "Today" : undefined}
                                            >
                                                <div className={cn("text-sm font-semibold", isToday && "text-sidebar")}>
                                                    {dayjs(d.date).format("D")}
                                                </div>
                                                <div className="text-[10px] font-normal uppercase">{d.day}</div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                                {grid.result.map((row, r) => (
                                    <tr key={row.employee.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40">
                                        <td
                                            className={cn(
                                                "sticky left-0 z-10 border-r border-gray-200 bg-white px-3 py-2 align-middle dark:border-gray-700 dark:bg-gray-900",
                                                stickyEmployeeShadow(gridScrolledX),
                                            )}
                                            style={{ width: EMPLOYEE_COL_W, minWidth: EMPLOYEE_COL_W, maxWidth: EMPLOYEE_COL_W }}
                                        >
                                            <div className="min-w-0">
                                                <EmployeeDetails
                                                    details={mapEmployeeForDetails(row.employee)}
                                                    size="xs"
                                                />
                                                {row.employee.department?.name && (
                                                    <p className="truncate text-[10px] text-muted-foreground ml-12 mt-0.5">
                                                        {row.employee.department.name}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        {row.cells.map((raw, c) => {
                                            const cell = resolveCell(raw, pending);
                                            const token = tokenFor(cell, shiftsById);
                                            const tone = cell.shift_id && !cell.is_weekoff && !cellShowsRosterLeave(cell)
                                                ? colorForShift(cell.shift_id, grid.shifts)
                                                : null;
                                            const editable = isEditable(r, c);
                                            const isToday = cell.date === todayIso;
                                            const isSelected = inSelection(r, c);
                                            const isAnchor = anchor?.r === r && anchor?.c === c;
                                            return (
                                                <td
                                                    key={cell.date}
                                                    data-roster-cell={`${r}-${c}`}
                                                    onMouseDown={(e) => handleCellMouseDown(r, c, e)}
                                                    onDoubleClick={() => {
                                                        if (!editable) {
                                                            explainWhyNotEditable({ row, cell });
                                                            return;
                                                        }
                                                        setCellTip(null);
                                                        setPicker({ r, c });
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const rowCells = row.cells.map((item) => resolveCell(item, pending));
                                                        setCellTip({
                                                            top: rect.top,
                                                            left: rect.left + rect.width / 2,
                                                            cell,
                                                            editable,
                                                            fallbackShiftId: inferShiftIdForLeave(cell, rowCells),
                                                            source: "hover",
                                                        });
                                                    }}
                                                    onMouseLeave={() => {
                                                        setCellTip((prev) => (prev?.source === "hover" ? null : prev));
                                                    }}
                                                    className={cn(
                                                        "relative h-10 border-r border-gray-200 px-3 py-0 text-center align-middle text-xs dark:border-gray-700 sm:px-6",
                                                        editable ? "cursor-pointer" : "cursor-not-allowed",
                                                        cell.is_locked && "bg-gray-100 text-gray-400 dark:bg-gray-800/70 dark:text-gray-500",
                                                        cell.is_holiday && "bg-rose-50 dark:bg-rose-900/20",
                                                        cell.is_pending && !cell.is_locked && "bg-amber-50 dark:bg-amber-900/20",
                                                        isToday && !tone && "bg-sidebar/5",
                                                    )}
                                                    style={{
                                                        width: DAY_COL_W,
                                                        minWidth: DAY_COL_W,
                                                        maxWidth: DAY_COL_W,
                                                        ...(tone ? { background: tone.bg, color: tone.text } : {}),
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <div
                                                            aria-hidden
                                                            className={cn(
                                                                "pointer-events-none absolute inset-0 ring-2 ring-inset",
                                                                isAnchor
                                                                    ? "ring-sidebar/50 dark:bg-gray-400/35 dark:ring-sidebar/300"
                                                                    : "ring-sidebar/30 dark:bg-gray-500/30 dark:ring-sidebar/400",
                                                            )}
                                                        />
                                                    )}
                                                    <div className="relative z-[1] mx-auto flex h-10 w-10 items-center justify-center">
                                                        <span className="font-semibold leading-none">{token}</span>
                                                    </div>
                                                    {cell.dirty && (
                                                        <span
                                                            className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-sidebar"
                                                            title="Unsaved"
                                                        />
                                                    )}
                                                    {cell.source === "override" && !cell.dirty && (
                                                        <span className="absolute bottom-0.5 right-0.5 text-[8px]">•</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer — row counts + paging */}
                {!loading && meta.total_count > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2 text-xs dark:border-gray-700">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
                            <span>
                                Showing{" "}
                                <span className="font-medium text-foreground">
                                    {rowRangeStart}–{rowRangeEnd}
                                </span>
                                {" "}of{" "}
                                <span className="font-medium text-foreground">{meta.total_count}</span>
                                {" "}employee{meta.total_count === 1 ? "" : "s"}
                            </span>
                            <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
                            <span>
                                {rowsOnPage} row{rowsOnPage === 1 ? "" : "s"} on this page
                            </span>
                            {grid.dates.length > 0 && (
                                <>
                                    <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
                                    <span>
                                        {grid.dates.length} day{grid.dates.length === 1 ? "" : "s"}
                                    </span>
                                </>
                            )}
                        </div>
                        {meta.no_of_pages > 1 && (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page.page_no <= 1 || loading}
                                    onClick={() => setPage((p) => ({ ...p, page_no: p.page_no - 1 }))}
                                >
                                    Previous
                                </Button>
                                <span className="text-muted-foreground whitespace-nowrap">
                                    Page {page.page_no} of {meta.no_of_pages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page.page_no >= meta.no_of_pages || loading}
                                    onClick={() => setPage((p) => ({ ...p, page_no: p.page_no + 1 }))}
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Shift picker */}
            <DialogModal
                open={!!picker}
                onClose={() => setPicker(null)}
                title={selection && selectionTargets.length > 1
                    ? `Set ${selectionTargets.length} cells`
                    : "Set shift"}
                showActions={false}
                width={pickerTab === "leave" && pickerLeaveRequests.length ? "480px" : "400px"}
            >
                <div className="flex flex-col gap-1">
                    {pickerLeaveRequests.length > 0 && (
                        <div className="mb-2 flex rounded-md border border-border p-0.5">
                            <button
                                type="button"
                                onClick={() => setPickerTab("shift")}
                                className={cn(
                                    "flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium",
                                    pickerTab === "shift"
                                        ? "bg-muted text-foreground"
                                        : "text-muted-foreground hover:text-foreground",
                                )}
                            >
                                <CalendarClock className="h-4 w-4" />
                                Set shift
                            </button>
                            <button
                                type="button"
                                onClick={() => setPickerTab("leave")}
                                className={cn(
                                    "relative flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium",
                                    pickerTab === "leave"
                                        ? "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                                        : "text-muted-foreground hover:text-foreground",
                                )}
                            >
                                <span className="relative inline-flex">
                                    <PlaneTakeoff className="h-4 w-4" />
                                    <span className="absolute -right-1 -top-1 flex h-2 w-2">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                                    </span>
                                </span>
                                Leave requests ({pickerLeaveRequests.length})
                            </button>
                        </div>
                    )}
                    {pickerTab === "leave" && pickerLeaveRequests.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">
                                Those days stay unchanged. Other selected days get the new shift or week off.
                            </p>
                            <div className="max-h-56 space-y-1 overflow-y-auto">
                                {pickerLeaveRequests.map((request) => {
                                    const statusLabel = LEAVE_REQUEST_STATUS_LABELS[request.approval_status]
                                        || request.approval_status;
                                    const isLoading = approvingLeaveId === request.id;
                                    return (
                                        <div
                                            key={request.id}
                                            className="flex items-start gap-2 rounded border border-amber-100 bg-amber-50/70 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/20"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-foreground">{request.employee_name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatLeaveRequestDates(request)}
                                                    {" · "}
                                                    {leaveRequestTypeText(request)}
                                                    {statusLabel ? ` · ${statusLabel}` : ""}
                                                </p>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-1">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={!!approvingLeaveId}
                                                    onClick={() => openLeaveReview(request)}
                                                    className="h-7 w-7 p-0"
                                                    title="View leave request"
                                                >
                                                    {isLoading
                                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        : <Eye className="h-3.5 w-3.5" />}
                                                </Button>
                                                {request.approval_status === "approved" && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={!!approvingLeaveId}
                                                        onClick={() => openLeaveRevert(request)}
                                                        className="h-7 w-7 p-0"
                                                        title="Revert approved leave"
                                                    >
                                                        <Rewind className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {pickerTab !== "leave" && pickerShifts.map((shift) => {
                        const tone = colorForShift(shift.id, grid.shifts);
                        return (
                            <button key={shift.id} type="button" onClick={() => handlePick(shift.id)}
                                className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                                <span className="inline-flex h-6 w-8 items-center justify-center rounded text-xs font-bold"
                                    style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.ring}` }}>
                                    {shift.code}
                                </span>
                                <span className="flex-1">{shift.name}</span>
                                {shift.shift_timings_from && (
                                    <span className="text-xs text-muted-foreground">
                                        {formatTimeRangeToAMPM(shift.shift_timings_from, shift.shift_timings_to)}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {pickerTab !== "leave" && (
                        <>
                            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                            {SPECIAL_OPTIONS.map((option) => (
                                <button key={option.value} type="button" onClick={() => handlePick(option.value)}
                                    className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                                    <span className="inline-flex h-6 w-8 items-center justify-center rounded border border-gray-300 text-xs font-bold dark:border-gray-600">
                                        {option.code}
                                    </span>
                                    <span className="flex-1">{option.label}</span>
                                </button>
                            ))}
                            <p className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                                <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                With the grid focused: arrows move, Enter opens this, a shift code's
                                first letter fills it in, W marks week off, Delete clears.
                            </p>
                        </>
                    )}
                </div>
            </DialogModal>

            <LeaveApprovalReviewDialog
                open={!!leaveReview}
                request={leaveReview}
                notes={leaveReviewNotes}
                onNotesChange={setLeaveReviewNotes}
                loading={leaveReviewLoading}
                onClose={() => {
                    setLeaveReview(null);
                    setLeaveReviewNotes("");
                }}
                onApprove={confirmLeaveReview}
                onReject={confirmLeaveReject}
            />

            <DialogModal
                open={!!leaveRevert}
                onClose={() => {
                    setLeaveRevert(null);
                    setLeaveRevertReason("");
                }}
                onSave={confirmLeaveRevert}
                title="Revert Approval"
                saveLabel="Revert"
                width="700px"
                overlayClassName="z-[70]"
                loading={leaveRevertLoading}
            >
                {leaveRevert && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950/30">
                            <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                You are about to revert the approved leave
                                {leaveRevert.request_from?.employee_name || leaveRevert.employee
                                    ? ` for ${leaveRevert.request_from?.employee_name || leaveRevert.employee}`
                                    : ""}
                                . After revert, you can assign a shift on those days. This cannot be undone.
                            </p>
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium">
                                Reason for reverting (optional)
                            </label>
                            <Textarea
                                value={leaveRevertReason}
                                onChange={(event) => setLeaveRevertReason(event.target.value)}
                                placeholder="Please provide a reason for reverting this approval..."
                                rows={3}
                                showCharCount
                                maxLength={250}
                            />
                        </div>
                    </div>
                )}
            </DialogModal>

            {cellTip && !picker && !importPreview && !leaveReview && !leaveRevert && (
                <div
                    role="tooltip"
                    className="pointer-events-none fixed z-40"
                    style={{
                        top: cellTip.top - 10,
                        left: cellTip.left,
                        transform: "translate(-50%, -100%)",
                    }}
                >
                    <div className="relative max-w-[240px]">
                        <div className="rounded-md border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md">
                            <CellTooltipContent
                                cell={cellTip.cell}
                                shiftsById={shiftsById}
                                editable={cellTip.editable}
                                fallbackShiftId={cellTip.fallbackShiftId}
                            />
                        </div>
                        <div
                            aria-hidden
                            className="absolute left-1/2 top-full -mt-[5px] h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-b border-r border-border bg-popover"
                        />
                    </div>
                </div>
            )}

            {/* Import preview */}
            <DialogModal
                open={!!importPreview}
                onClose={() => setImportPreview(null)}
                onSave={confirmImport}
                title="Review the imported sheet"
                saveLabel={`Apply ${importPreview?.server?.would_apply ?? importPreview?.cells?.length ?? 0} change(s)`}
                hideSaveButton={!importPreview?.server?.would_apply}
                loading={saving}
                width="560px"
                editMode
            >
                <div className="flex flex-col gap-3 text-xs">
                    <div className="flex flex-wrap gap-3">
                        <span className="rounded bg-green-100 px-2 py-1 font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            {importPreview?.server?.would_apply ?? 0} ready to apply
                        </span>
                        {!!importPreview?.server?.skipped?.length && (
                            <span className="rounded bg-amber-100 px-2 py-1 font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                {importPreview.server.skipped.length} skipped by the server
                            </span>
                        )}
                        {!!importPreview?.problems?.length && (
                            <span className="rounded bg-rose-100 px-2 py-1 font-medium text-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
                                {importPreview.problems.length} unreadable row(s)
                            </span>
                        )}
                    </div>

                    {(importPreview?.problems?.length || importPreview?.server?.skipped?.length) ? (
                        <div className="max-h-64 overflow-auto rounded border border-gray-200 dark:border-gray-700">
                            <table className="w-full text-[11px]">
                                <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr>
                                        <th className="px-2 py-1 text-left">Row / Employee</th>
                                        <th className="px-2 py-1 text-left">Date</th>
                                        <th className="px-2 py-1 text-left">Why it was skipped</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(importPreview?.problems || []).map((problem, index) => (
                                        <tr key={`p${index}`} className="border-t border-gray-100 dark:border-gray-800">
                                            <td className="px-2 py-1">{problem.code || problem.row || "—"}</td>
                                            <td className="px-2 py-1">{problem.date || "—"}</td>
                                            <td className="px-2 py-1">{problem.reason}</td>
                                        </tr>
                                    ))}
                                    {(importPreview?.server?.skipped || []).map((skip, index) => (
                                        <tr key={`s${index}`} className="border-t border-gray-100 dark:border-gray-800">
                                            <td className="px-2 py-1">{skip.employee}</td>
                                            <td className="px-2 py-1">{skip.date}</td>
                                            <td className="px-2 py-1">{skip.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}

                    <p className="text-[11px] text-muted-foreground">
                        Nothing has been saved yet. Cells that already match the roster are not
                        counted as changes.
                    </p>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default DutyRoster;
